import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Reusable Delete button + reason dialog for any transaction record.
 *
 * Props:
 *   module   : one of gate_entry|grn|quality_inspection|material_request|
 *              issue|challan|inventory_adjustment|purchase_order
 *   record   : the record object (must have id and created_at)
 *   label    : human label shown in the modal (e.g. "GRN-2026-00012")
 *   onDeleted: callback after success (request created OR instant SA delete)
 *
 * Visibility: probes /api/delete-requests/can-delete; renders nothing if not allowed.
 */
export default function DeleteRequestButton({ module, record, label, onDeleted }) {
  const { user } = useAuth();
  const isSA = !!user?.role?.is_super_admin;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!record?.id || record?.deleted_at) { setAllowed(false); return; }
    let cancelled = false;
    api.get(`/delete-requests/can-delete?module=${module}&record_id=${record.id}`)
      .then((r) => { if (!cancelled) setAllowed(!!r.data.allowed); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, [module, record?.id, record?.deleted_at]);

  if (!allowed) return null;

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Please provide a reason (min 3 chars).");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/delete-requests", { module, record_id: record.id, reason });
      if (res.data.status === "deleted") {
        toast.success("Deleted.");
      } else {
        toast.success("Delete request sent to Super Admin for approval.");
      }
      setOpen(false); setReason("");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title={isSA ? "Delete (instant, with reason)" : "Request deletion"}
        className="text-[#C62828] hover:bg-[#C62828]/10 p-1 rounded-sm" data-testid={`delete-${module}-${record.id}`}>
        <Trash2 size={14}/>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#0D2B5E]/40 backdrop-blur-sm flex items-center justify-center p-8" data-testid="delete-request-modal">
          <div className="bg-white border border-[#EEEEEE] rounded-sm shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between border-b border-[#EEEEEE] pb-3 mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#C62828]">{isSA ? "Delete record" : "Request deletion"}</div>
                <div className="text-base font-bold text-[#0D2B5E] font-mono">{label || record.id}</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#0D2B5E]/60 hover:text-[#C62828] p-1.5"><X size={18}/></button>
            </div>
            <div className="text-sm text-[#0D2B5E]/80 mb-3 leading-relaxed">
              {isSA
                ? <>This will <strong>soft-delete</strong> the record immediately. The data stays in the database and can be restored from Settings → Deleted Records.</>
                : <>Your request goes to <strong>Super Admin</strong> for approval. The record stays unchanged until then. You will be notified on the Pending Requests queue.</>}
            </div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Reason (mandatory)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500}
              className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" placeholder="Why is this entry being deleted?"
              data-testid="delete-reason-input"/>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
              <button onClick={submit} disabled={busy || reason.trim().length < 3}
                className="px-5 py-2 bg-[#C62828] hover:bg-[#8B1B1B] disabled:bg-[#BBB] text-white text-sm rounded-sm font-bold uppercase tracking-wider"
                data-testid="delete-request-submit">
                {busy ? "Working…" : (isSA ? "Delete now" : "Send for approval")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
